import Heatmap from 'ol/layer/Heatmap';
import WebGLPointsLayerRenderer from 'ol/renderer/webgl/PointsLayer';
import { clamp } from 'ol/math';

function createGradient(colors: string[]): HTMLCanvasElement {
    const width = 1;
    const height = 256;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d')!;
    const gradient = context.createLinearGradient(0, 0, 0, height);
    const step = 1 / (colors.length - 1);
    for (let i = 0; i < colors.length; ++i) {
        gradient.addColorStop(i * step, colors[i]);
    }
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);
    return canvas;
}

class VariableRadiusHeatmap extends Heatmap {
    constructor(options: any) {
        super(options);
        const gradient = options.gradient ?? ['#00f', '#0ff', '#0f0', '#ff0', '#f00'];
        (this as any).gradient_ = createGradient(gradient);
    }

    override createRenderer(): WebGLPointsLayerRenderer {
        return new WebGLPointsLayerRenderer(this, {
            className: this.getClassName(),
            attributes: [
                {
                    name: 'weight',
                    callback: (feature) => {
                        const weight = this.weightFunction_(feature);
                        return weight !== undefined ? clamp(weight, 0, 1) : 1;
                    },
                },
                {
                    name: 'radius',
                    callback: (feature) => {
                        const weight = this.weightFunction_(feature) || 0;
                        return 8 + 10 * clamp(weight, 0, 1);
                    },
                },
            ],
            vertexShader: `
        precision mediump float;
        uniform mat4 u_projectionMatrix;
        uniform mat4 u_offsetScaleMatrix;
        uniform float u_blur;
        attribute vec2 a_position;
        attribute float a_index;
        attribute float a_weight;
        attribute float a_radius;
        attribute vec4 a_hitColor;

        varying vec2 v_texCoord;
        varying float v_weight;
        varying vec4 v_hitColor;

        void main(void) {
          float size = (a_radius + u_blur) * 2.0;
          float offsetX = a_index == 0.0 || a_index == 3.0 ? -size / 2.0 : size / 2.0;
          float offsetY = a_index == 0.0 || a_index == 1.0 ? -size / 2.0 : size / 2.0;
          vec4 offsets = u_offsetScaleMatrix * vec4(offsetX, offsetY, 0.0, 0.0);
          gl_Position = u_projectionMatrix * vec4(a_position, 0.0, 1.0) + offsets;
          float u = a_index == 0.0 || a_index == 3.0 ? 0.0 : 1.0;
          float v = a_index == 0.0 || a_index == 1.0 ? 0.0 : 1.0;
          v_texCoord = vec2(u, v);
          v_hitColor = a_hitColor;
          v_weight = a_weight;
        }
      `,
            fragmentShader: `
        precision mediump float;
        uniform float u_blurSlope;
        uniform mediump int u_hitDetection;

        varying vec2 v_texCoord;
        varying float v_weight;
        varying vec4 v_hitColor;

        void main(void) {
          vec2 texCoord = v_texCoord * 2.0 - vec2(1.0, 1.0);
          float sqRadius = texCoord.x * texCoord.x + texCoord.y * texCoord.y;
          float value = (1.0 - sqrt(sqRadius)) * u_blurSlope;
          float alpha = smoothstep(0.0, 1.0, value) * v_weight;
          gl_FragColor = vec4(alpha, alpha, alpha, alpha);
          if (u_hitDetection > 0) {
            if (alpha < 0.05) discard;
            gl_FragColor = v_hitColor;
          }
        }
      `,
            uniforms: {
                u_blur: () => this.get('blur') ?? 15,
                u_blurSlope: () => 1.0,
            },
            postProcesses: [
                {
                    fragmentShader: `
            precision mediump float;
            uniform sampler2D u_image;
            uniform sampler2D u_gradientTexture;
            uniform float u_opacity;
            varying vec2 v_texCoord;

            void main() {
              vec4 color = texture2D(u_image, v_texCoord);
              gl_FragColor.a = color.a * u_opacity;
              gl_FragColor.rgb = texture2D(u_gradientTexture, vec2(0.5, color.a)).rgb;
              gl_FragColor.rgb *= gl_FragColor.a;
            }
          `,
                    uniforms: {
                        u_gradientTexture: () => (this as any).gradient_,
                        u_opacity: () => this.getOpacity(),
                    },
                },
            ],
        });
    }
}

export default VariableRadiusHeatmap;
